import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Card,
  Row,
  Col,
  Spinner,
  Button,
  Toast,
  ToastContainer,
  Form,
} from "react-bootstrap";
import { useNavigate, useLocation, useOutletContext } from "react-router-dom";
import Header from "../components/Header";
import { getHost } from "../utils/utils";
import {
  FiActivity,
  FiCheckCircle,
  FiAlertTriangle,
  FiWifi,
  FiRefreshCw,
  FiWifiOff,
  FiSearch,
  FiShield,
  FiZap,
  FiPower,
  FiBell,
  FiBellOff,
} from "react-icons/fi";

const API_OVERVIEW = `${getHost()}/api/metrics/overview`;
const API_BANDWIDTH = `${getHost()}/api/metrics/bandwidth?hours=24&proxy_type=resi`;
const API_MONITORS = `${getHost()}/api/monitors?runtime_type=k8s`;
const getToken = () => localStorage.getItem("jwt");
const REFRESH_INTERVAL_MS = 60000;

const STATUS_COLORS = {
  healthy: "#00E98E",
  amber: "#FFC107",
  alerting: "#FF4444",
};

const FILTERS = {
  all: "All",
  healthy: "Healthy",
  amber: "Amber",
  alerting: "Alerting",
  offline: "Offline",
  disabled: "Disabled",
  slow: "Slow (>30s)",
  muted: "Muted",
};

function formatBytes(bytes) {
  if (bytes >= 1e9) return `${(bytes / 1e9).toFixed(2)} GB`;
  if (bytes >= 1e6) return `${(bytes / 1e6).toFixed(2)} MB`;
  return `${(bytes / 1e3).toFixed(2)} KB`;
}

function formatPercent(rate) {
  return `${(rate * 100).toFixed(1)}%`;
}

function formatRelativeTime(ts) {
  if (!ts) return "N/A";
  const diff = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
  if (diff < 60) return "just now";
  const mins = Math.floor(diff / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  const remainMins = mins % 60;
  if (hours < 24)
    return remainMins > 0 ? `${hours}h ${remainMins}m ago` : `${hours}h ago`;
  const days = Math.floor(hours / 24);
  const remainHours = hours % 24;
  return remainHours > 0 ? `${days}d ${remainHours}h ago` : `${days}d ago`;
}

function isOffline(ts) {
  if (!ts) return true;
  return Date.now() - new Date(ts).getTime() > 15 * 60 * 1000;
}

// The backend smooths error_rate and applies hysteresis, so `status` is the
// authoritative value. The local thresholds stay as a fallback for a metrics
// payload that predates it.
function getStatusFilter(metric) {
  if (metric.enabled === false) return "disabled";
  if (metric.status) return metric.status;
  if (isOffline(metric.ts)) return "offline";
  if (metric.error_rate >= 0.3) return "alerting";
  if (metric.error_rate >= 0.1) return "amber";
  return "healthy";
}

function displayedErrorRate(metric) {
  return metric.smoothed_error_rate ?? metric.error_rate ?? 0;
}

const SummaryCard = ({ icon, label, value, color }) => (
  <Col lg={3} md={4} sm={6} xs={6} className="mb-3">
    <Card className="border border-dark bg-transparent h-100">
      <Card.Body className="p-3">
        <div className="d-flex align-items-center gap-2 mb-2">
          {icon}
          <span className="text-white-65 fs-12">{label}</span>
        </div>
        <h4 className="fw-600 mb-0" style={{ color: color || "#ADADAD" }}>
          {value}
        </h4>
      </Card.Body>
    </Card>
  </Col>
);

const MonitorCard = ({
  metric,
  onClick,
  onRestart,
  restarting,
  onToggle,
  toggling,
  rateLimit,
  canMute,
  muted,
  onMute,
  muting,
}) => {
  const status = getStatusFilter(metric);
  const disabled = status === "disabled";
  const offline = status === "offline";
  const color = disabled
    ? "#555"
    : offline
      ? "#FF4444"
      : STATUS_COLORS[status] || STATUS_COLORS.healthy;
  const errorPct = disabled
    ? 0
    : Math.min(displayedErrorRate(metric) * 100, 100);
  const hasCfOrImperva = metric.cf_solves > 0 || metric.imperva_solves > 0;
  const dimStyle = disabled ? { opacity: 0.45 } : {};

  return (
    <Col xl={3} lg={4} md={6} className="mb-3">
      <Card
        className="border bg-transparent h-100 cursor-pointer"
        style={{
          borderColor: disabled ? "#ffffff0a" : `${color}33`,
          overflow: "hidden",
        }}
        onClick={disabled ? undefined : onClick}
      >
        {/* Error rate bar — top edge */}
        <div
          style={{
            height: 3,
            background: disabled
              ? "#ffffff08"
              : `linear-gradient(to right, ${color} ${errorPct}%, transparent ${errorPct}%)`,
          }}
        />
        <Card.Body className="p-3 d-flex flex-column">
          {/* Row 1: Name + status dot + error % */}
          <div className="d-flex justify-content-between align-items-center mb-2">
            <div
              className="d-flex align-items-center gap-2"
              style={{ minWidth: 0, ...dimStyle }}
            >
              <div
                style={{
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  backgroundColor: color,
                  flexShrink: 0,
                }}
              />
              <span
                className="fs-13 fw-600"
                style={{
                  color: disabled ? "#555" : "#ADADAD",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
                title={metric.monitor}
              >
                {metric.monitor}
              </span>
            </div>
            {!disabled && (
              <span
                className="fs-11 fw-600 flex-shrink-0 ms-2"
                style={{ color }}
              >
                {formatPercent(displayedErrorRate(metric))}
              </span>
            )}
          </div>

          {/* Row 2: Compact metadata line */}
          {disabled ? (
            <div style={{ marginBottom: 6 }}>
              <span className="fs-11" style={{ color: "#555" }}>
                Disabled
              </span>
            </div>
          ) : (
            <div
              className="d-flex align-items-center gap-2 flex-wrap"
              style={{ marginBottom: 6 }}
            >
              {metric.proxy_type && (
                <span className="fs-11" style={{ color: "#7C98FF" }}>
                  {metric.proxy_type.toUpperCase()}
                </span>
              )}
              {metric.cb_state && metric.cb_state !== "closed" && (
                <span
                  className="fs-11"
                  style={{
                    color:
                      metric.cb_state === "half_open" ? "#FFC107" : "#FF4444",
                  }}
                >
                  CB{" "}
                  {metric.cb_state === "half_open" ? "half" : metric.cb_state}
                </span>
              )}
              {offline && (
                <span className="fs-11 fw-500" style={{ color: "#FF4444" }}>
                  OFFLINE
                </span>
              )}
              {canMute && muted && (
                <span className="fs-11 fw-500" style={{ color: "#FFC107" }}>
                  MUTED
                </span>
              )}
              {metric.restart_count > 0 && (
                <span className="fs-11" style={{ color: "#FFC107" }}>
                  {metric.restart_count}x restart
                </span>
              )}
              {hasCfOrImperva && (
                <span className="fs-11" style={{ color: "#FFC107" }}>
                  {metric.cf_solves > 0 && (
                    <>
                      <FiShield style={{ fontSize: 10, marginRight: 2 }} />
                      {metric.cf_solves}
                    </>
                  )}
                  {metric.cf_solves > 0 && metric.imperva_solves > 0 && " "}
                  {metric.imperva_solves > 0 && (
                    <>
                      <FiZap style={{ fontSize: 10, marginRight: 2 }} />
                      {metric.imperva_solves}
                    </>
                  )}
                </span>
              )}
              {metric.avg_cycle_time && (
                <span className="fs-11" style={{ color: metric.avg_cycle_time > 60 ? "#FF4444" : metric.avg_cycle_time > 30 ? "#FFC107" : "#848484" }}>
                  {metric.avg_cycle_time > 60 ? `${(metric.avg_cycle_time / 60).toFixed(1)}m` : `${Math.round(metric.avg_cycle_time)}s`} cycle
                </span>
              )}
              {rateLimit && (() => {
                const expired = !rateLimit.resets_at || rateLimit.resets_at < Date.now() / 1000;
                const remaining = expired ? rateLimit.max : rateLimit.remaining;
                const mins = expired ? 0 : Math.max(0, Math.ceil((rateLimit.resets_at - Date.now() / 1000) / 60));
                return (
                  <span
                    className="fs-11"
                    style={{ color: remaining === 0 ? "#FF4444" : "#848484" }}
                  >
                    {remaining}/{rateLimit.max} solves
                    {mins > 0 && ` · resets in ${mins}m`}
                  </span>
                );
              })()}
            </div>
          )}

          {/* Row 3: Running since + actions */}
          <div className="d-flex justify-content-between align-items-center mt-auto">
            <span className="text-white-65 fs-11" style={dimStyle}>
              {disabled
                ? ""
                : metric.started_at
                  ? formatRelativeTime(metric.started_at)
                  : "N/A"}
            </span>
            <div className="d-flex align-items-center gap-2">
              {canMute && (
                <Button
                  variant=""
                  size="sm"
                  className="fs-11 d-flex align-items-center p-0 card-action-btn"
                  style={{
                    color: muted ? "#FFC107" : "#848484",
                    border: "none",
                    background: "none",
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onMute(e);
                  }}
                  disabled={muting}
                  title={muted ? "Unmute webhooks" : "Mute webhooks"}
                >
                  {muting ? (
                    <Spinner size="sm" style={{ width: 11, height: 11 }} />
                  ) : muted ? (
                    <FiBellOff style={{ fontSize: 12 }} />
                  ) : (
                    <FiBell style={{ fontSize: 12 }} />
                  )}
                </Button>
              )}
              {!disabled && (
                <Button
                  variant=""
                  size="sm"
                  className="fs-11 d-flex align-items-center gap-1 p-0 card-action-btn"
                  style={{
                    color: "#848484",
                    border: "none",
                    background: "none",
                  }}
                  onClick={(e) => onRestart(e)}
                  disabled={restarting}
                  title="Restart monitor"
                >
                  {restarting ? (
                    <Spinner size="sm" style={{ width: 11, height: 11 }} />
                  ) : (
                    <FiRefreshCw style={{ fontSize: 11 }} />
                  )}
                </Button>
              )}
              <Button
                variant=""
                size="sm"
                className="fs-11 d-flex align-items-center p-0 card-action-btn"
                style={{
                  color: disabled ? "#00E98E" : "#FF4444",
                  border: "none",
                  background: "none",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!disabled && !window.confirm(`Disable ${metric.monitor}?`)) return;
                  onToggle(e);
                }}
                disabled={toggling}
                title={disabled ? "Enable monitor" : "Disable monitor"}
              >
                {toggling ? (
                  <Spinner size="sm" style={{ width: 11, height: 11 }} />
                ) : (
                  <FiPower style={{ fontSize: 12 }} />
                )}
              </Button>
            </div>
          </div>
        </Card.Body>
      </Card>
    </Col>
  );
};

const MonitorHealth = () => {
  const { toggleSidebar } = useOutletContext();
  const [metrics, setMetrics] = useState([]);
  const [resiBandwidth, setResiBandwidth] = useState(0);
  const [loading, setLoading] = useState(true);
  const [monitorMap, setMonitorMap] = useState({});
  const [monitorEnabled, setMonitorEnabled] = useState({});
  const [monitorMuted, setMonitorMuted] = useState({});
  const [monitorCategory, setMonitorCategory] = useState({});
  const [mutingMonitor, setMutingMonitor] = useState(null);
  const [rateLimits, setRateLimits] = useState({});
  const [restartingMonitor, setRestartingMonitor] = useState(null);
  const [togglingMonitor, setTogglingMonitor] = useState(null);
  const [restartMessage, setRestartMessage] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const navigate = useNavigate();
  const location = useLocation();
  const isAdminView = location.pathname.startsWith("/admin");
  const basePath = isAdminView ? "/admin/health" : "/monitors/health";

  const fetchData = useCallback(async () => {
    const headers = { Authorization: `Bearer ${getToken()}` };

    try {
      const [overviewRes, bandwidthRes, monitorsRes, limitsRes] = await Promise.all([
        fetch(API_OVERVIEW, { headers }),
        fetch(API_BANDWIDTH, { headers }),
        fetch(API_MONITORS, { headers }),
        fetch(`${getHost()}/api/admin/rate-limits`, { headers }).catch(() => null),
      ]);

      if (overviewRes.ok) {
        const overviewData = await overviewRes.json();
        setMetrics(overviewData.metrics || []);
      }

      if (bandwidthRes.ok) {
        const bandwidthData = await bandwidthRes.json();
        const total = (bandwidthData.bandwidth || []).reduce(
          (sum, b) => sum + (b.total_bytes_in || 0),
          0,
        );
        setResiBandwidth(total);
      }

      if (monitorsRes.ok) {
        const monitorsData = await monitorsRes.json();
        const map = {};
        const enabledMap = {};
        const mutedMap = {};
        const categoryMap = {};
        (monitorsData.monitors || []).forEach((m) => {
          map[m.name] = m._id || m.folder_name;
          enabledMap[m.name] = m.enabled !== false;
          mutedMap[m.name] = m.mute_webhooks === true;
          categoryMap[m.name] = m.category || "Not Set";
        });
        setMonitorMap(map);
        setMonitorEnabled(enabledMap);
        setMonitorMuted(mutedMap);
        setMonitorCategory(categoryMap);
      }

      if (limitsRes?.ok) {
        const limitsData = await limitsRes.json();
        const limitsMap = {};
        (limitsData.limits || []).forEach((l) => {
          limitsMap[l.monitor] = l;
        });
        setRateLimits(limitsMap);
      }
    } catch {
      // Keep existing state on failure
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleRestart = async (e, monitorName, monitorId) => {
    e.stopPropagation();
    setRestartingMonitor(monitorName);
    setRestartMessage("");
    try {
      const res = await fetch(
        `${getHost()}/api/monitors/${monitorId}/restart`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${getToken()}`,
            "Content-Type": "application/json",
          },
        },
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setRestartMessage(`Restart command sent for ${monitorName}`);
      setTimeout(() => setRestartMessage(""), 3000);
      setTimeout(() => setRestartingMonitor(null), 5000);
    } catch (error) {
      setRestartMessage(`Failed to restart ${monitorName}: ${error.message}`);
      setRestartingMonitor(null);
    }
  };

  const handleToggle = async (e, monitorName, monitorId) => {
    e.stopPropagation();
    setTogglingMonitor(monitorName);
    try {
      const res = await fetch(`${getHost()}/api/monitors/${monitorId}/toggle`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${getToken()}`,
          "Content-Type": "application/json",
        },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setMonitorEnabled((prev) => ({ ...prev, [monitorName]: data.enabled }));
      setRestartMessage(
        `${monitorName} ${data.enabled ? "enabled" : "disabled"}`,
      );
      setTimeout(() => setRestartMessage(""), 3000);
    } catch (error) {
      setRestartMessage(`Failed to toggle ${monitorName}: ${error.message}`);
    } finally {
      setTogglingMonitor(null);
    }
  };

  const handleMute = async (e, monitorName, monitorId) => {
    e.stopPropagation();
    const muted = !monitorMuted[monitorName];
    if (
      !muted &&
      !window.confirm(
        `Unmute ${monitorName}? Its clients will start receiving pings again within a minute, including anything it detects right after.`,
      )
    )
      return;
    setMutingMonitor(monitorName);
    try {
      const res = await fetch(
        `${getHost()}/api/admin/monitors/${monitorId}/mute`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${getToken()}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ muted }),
        },
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setMonitorMuted((prev) => ({ ...prev, [monitorName]: data.mute_webhooks }));
      setRestartMessage(
        `${monitorName} ${data.mute_webhooks ? "muted" : "unmuted"} — applied within a minute`,
      );
      setTimeout(() => setRestartMessage(""), 3000);
    } catch (error) {
      setRestartMessage(`Failed to update mute for ${monitorName}: ${error.message}`);
    } finally {
      setMutingMonitor(null);
    }
  };

  // Merge metrics with disabled monitors (which have no metrics)
  const allMonitorEntries = useMemo(() => {
    const knownMonitorNames = new Set(Object.keys(monitorEnabled));
    const metricsMap = {};
    metrics.forEach((m) => {
      if (knownMonitorNames.size > 0 && !knownMonitorNames.has(m.monitor)) {
        return;
      }
      metricsMap[m.monitor] = m;
    });

    const entries = Object.values(metricsMap).map((m) => ({
      ...m,
      enabled: monitorEnabled[m.monitor] !== false,
    }));

    // Add monitors with no metrics — disabled OR enabled-but-no-recent-snapshot.
    // Without this branch covering the enabled case, a monitor between toggle
    // and first flush is in neither metricsMap nor the disabled bucket and
    // vanishes from every tab, including "All".
    Object.entries(monitorEnabled).forEach(([name, enabled]) => {
      if (!metricsMap[name]) {
        entries.push({
          monitor: name,
          error_rate: 0,
          ts: null,
          enabled,
        });
      }
    });

    if (categoryFilter === "all") return entries;
    return entries.filter((m) => monitorCategory[m.monitor] === categoryFilter);
  }, [metrics, monitorEnabled, monitorCategory, categoryFilter]);

  const categoryCounts = useMemo(() => {
    const counts = {};
    Object.keys(monitorEnabled).forEach((name) => {
      const cat = monitorCategory[name] || "Not Set";
      counts[cat] = (counts[cat] || 0) + 1;
    });
    return Object.entries(counts).sort(([a], [b]) => a.localeCompare(b));
  }, [monitorEnabled, monitorCategory]);

  const totalMonitors = allMonitorEntries.length;
  const statusCounts = useMemo(() => {
    const counts = {
      healthy: 0,
      amber: 0,
      alerting: 0,
      offline: 0,
      disabled: 0,
    };
    allMonitorEntries.forEach((m) => {
      const status = getStatusFilter(m);
      if (status in counts) counts[status] += 1;
    });
    return counts;
  }, [allMonitorEntries]);
  const {
    healthy: healthyCount,
    amber: amberCount,
    alerting: alertingCount,
    offline: offlineCount,
    disabled: disabledCount,
  } = statusCounts;

  const filteredMetrics = useMemo(() => {
    const searchLower = search.toLowerCase();
    return [...allMonitorEntries]
      .filter((m) => {
        if (search && !m.monitor.toLowerCase().includes(searchLower))
          return false;
        if (statusFilter === "slow")
          return m.avg_cycle_time && m.avg_cycle_time > 30;
        if (statusFilter === "muted") return monitorMuted[m.monitor] === true;
        if (statusFilter !== "all" && getStatusFilter(m) !== statusFilter)
          return false;
        return true;
      })
      .sort((a, b) => {
        // Disabled always at the bottom
        if (a.enabled === false && b.enabled !== false) return 1;
        if (a.enabled !== false && b.enabled === false) return -1;
        if (statusFilter === "slow")
          return (b.avg_cycle_time || 0) - (a.avg_cycle_time || 0);
        return displayedErrorRate(b) - displayedErrorRate(a);
      });
  }, [allMonitorEntries, search, statusFilter, monitorMuted]);

  return (
    <div className="w-100 monitor-setting">
      <Header toggleSidebar={toggleSidebar} />
      <div className="pb-4 mx-4 content-body">
        <Row className="gx-0 align-items-center">
          <Col md={12}>
            <div className="mb-2">
              <div className="d-inline-flex align-items-center justify-content-center bg-dark rounded p-2 mb-2">
                <FiActivity className="text-white" />
              </div>
              <h4 className="fs-22 text-main mb-1">Monitor Health</h4>
              <p className="text-white-65 fs-14 mb-0">
                Real-time health overview of all monitors
              </p>
            </div>
          </Col>
        </Row>
      </div>

      <div className="px-4 mb-4">
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" />
          </div>
        ) : (
          <>
            <Row className="mb-4">
              <SummaryCard
                icon={<FiActivity className="text-gray fs-14" />}
                label="Total Monitors"
                value={totalMonitors}
              />
              <SummaryCard
                icon={
                  <FiCheckCircle
                    style={{ color: STATUS_COLORS.healthy }}
                    className="fs-14"
                  />
                }
                label="Healthy"
                value={healthyCount}
                color={STATUS_COLORS.healthy}
              />
              <SummaryCard
                icon={
                  <FiAlertTriangle
                    style={{ color: STATUS_COLORS.amber }}
                    className="fs-14"
                  />
                }
                label="Amber"
                value={amberCount}
                color={STATUS_COLORS.amber}
              />
              <SummaryCard
                icon={
                  <FiAlertTriangle
                    style={{ color: STATUS_COLORS.alerting }}
                    className="fs-14"
                  />
                }
                label="Alerting"
                value={alertingCount}
                color={STATUS_COLORS.alerting}
              />
              <SummaryCard
                icon={
                  <FiWifiOff style={{ color: "#FF4444" }} className="fs-14" />
                }
                label="Offline"
                value={offlineCount}
                color={offlineCount > 0 ? "#FF4444" : "#ADADAD"}
              />
              <SummaryCard
                icon={<FiPower style={{ color: "#555" }} className="fs-14" />}
                label="Disabled"
                value={disabledCount}
                color="#555"
              />
              <SummaryCard
                icon={<FiWifi className="fs-14" style={{ color: "#7C98FF" }} />}
                label="Resi Bandwidth (24h)"
                value={formatBytes(resiBandwidth)}
                color="#7C98FF"
              />
            </Row>

            {/* Search + filter bar */}
            <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
              <div
                className="position-relative health-search"
                style={{ maxWidth: 260, flex: 1 }}
              >
                <FiSearch
                  style={{
                    position: "absolute",
                    left: 10,
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "#848484",
                    fontSize: 13,
                  }}
                />
                <Form.Control
                  type="text"
                  placeholder="Search monitors..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="fs-12"
                  style={{
                    paddingLeft: 32,
                    backgroundColor: "transparent",
                    border: "1px solid #ffffff1f",
                    color: "#ADADAD",
                    height: 34,
                  }}
                />
              </div>
              <Form.Select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="fs-12"
                style={{
                  backgroundColor: "transparent",
                  border: "1px solid #ffffff1f",
                  color: "#ADADAD",
                  height: 34,
                  width: 180,
                }}
              >
                <option value="all">All categories</option>
                {categoryCounts.map(([cat, count]) => (
                  <option key={cat} value={cat}>
                    {cat} ({count})
                  </option>
                ))}
              </Form.Select>
              <div className="d-flex gap-1 health-filter-chips">
                {Object.entries(FILTERS)
                  .filter(([key]) => key !== "muted" || isAdminView)
                  .map(([key, label]) => (
                  <Button
                    key={key}
                    variant=""
                    size="sm"
                    className="fs-11 px-2 py-1"
                    style={{
                      color: statusFilter === key ? "#fff" : "#848484",
                      backgroundColor:
                        statusFilter === key ? "#ffffff14" : "transparent",
                      border:
                        statusFilter === key
                          ? "1px solid #ffffff1f"
                          : "1px solid transparent",
                      borderRadius: 4,
                    }}
                    onClick={() => setStatusFilter(key)}
                  >
                    {label}
                  </Button>
                ))}
              </div>
              <span className="text-white-65 fs-11 ms-auto">
                {filteredMetrics.length}/{totalMonitors}
              </span>
            </div>

            <ToastContainer
              position="top-end"
              className="p-3"
              style={{ zIndex: 9999, position: "fixed" }}
            >
              <Toast
                show={!!restartMessage}
                onClose={() => setRestartMessage("")}
                autohide
                delay={4000}
                bg={restartMessage.startsWith("Failed") ? "danger" : "success"}
              >
                <Toast.Body className="text-white">{restartMessage}</Toast.Body>
              </Toast>
            </ToastContainer>

            <Row>
              {filteredMetrics.map((metric) => (
                <MonitorCard
                  key={metric.monitor}
                  metric={metric}
                  rateLimit={rateLimits[metric.monitor]}
                  onClick={() =>
                    navigate(
                      `${basePath}/${encodeURIComponent(metric.monitor)}`,
                    )
                  }
                  onRestart={(e) =>
                    handleRestart(e, metric.monitor, monitorMap[metric.monitor])
                  }
                  restarting={restartingMonitor === metric.monitor}
                  onToggle={(e) =>
                    handleToggle(e, metric.monitor, monitorMap[metric.monitor])
                  }
                  toggling={togglingMonitor === metric.monitor}
                  canMute={isAdminView}
                  muted={monitorMuted[metric.monitor] === true}
                  onMute={(e) =>
                    handleMute(e, metric.monitor, monitorMap[metric.monitor])
                  }
                  muting={mutingMonitor === metric.monitor}
                />
              ))}
            </Row>
          </>
        )}
      </div>
    </div>
  );
};

export default MonitorHealth;
